<script setup>
import { ref, onBeforeUnmount, watch, computed } from 'vue'
import { useProfileStore }      from '@/stores/profile'
import { useTranslaterStore }   from '@/stores/translater'
import { usePointsStore }       from '@/stores/points'
import { useCarHistoryStore }   from '@/stores/car-history'
import UTACarHistoryGraph       from '@/components/car/UTACarHistoryGraph.vue'
import KANChosen                from '@/components/KANChosen.vue'
import checkArrayIncludes       from '@/helpers/checkArrayIncludes'
import calcDatas                from '@/helpers/calcDatas'

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const profileStore      = useProfileStore()
const translaterStore   = useTranslaterStore()
const pointsStore       = usePointsStore()
const carHistoryStore   = useCarHistoryStore()

const profile                   = computed(() => profileStore.profile)
const getTranslate              = computed(() => translaterStore.getTranslate)
const carState                  = computed(() => pointsStore.carState)
const statesHistory             = computed(() => carHistoryStore.statesHistory)
const statesHistoryMsg          = computed(() => carHistoryStore.statesHistoryMsg)
const statesHistoryProcess      = computed(() => carHistoryStore.statesHistoryProcess)
const statesHistoryPart         = computed(() => carHistoryStore.statesHistoryPart)
const UTACarHistoryMaxDateFrom  = computed(() => calcDatas('tm', new Date().getTime()))
const UTACarHistoryMaxDateTill  = computed(() => calcDatas('tEnd'))

const carStates = computed(() => {

    const states = carState.value

    const excludeItems = [
        'ENGINE_ON',                    // Время запуска двигателя
        'ENGINE_OFF',                   // Время остановки двигателя
        'START_MOVING',                 // Время начала движения
        'STOP_MOVING',                  // Время остановки
        'LAST_DIAGNOSTICS_TIME',        // Последнее обновление диагностики [Обновление Diag-CAN]
        'LAST_NAVIGATION_TIME',         // Последнее обновление навигации
        'LAST_CAN_PARAM_TIME',          // Последнее обновление параметров автомобиля [Обновление Body-CAN]
        'LAST_ONLINE',                  // Последний выход на связь
        'MILEAGE_ON_FUEL_RESIDUES'      // Пробег на остатке топлива [старый параметр]
    ]

    // Параметры по правам доступа пользователя
    const firmwareItems = ['ANS1', 'ANS2', 'ANS3', 'ANS4', 'ANS5', 'DIG1', 'DIG2', 'DIG3', 'DIG4', 'DIG5']

    // Исключаемые параметры
    const excludeArr = !checkArrayIncludes(profile.value.attr?.array, ['FIRMWARE']) ? [...excludeItems, ...firmwareItems] : excludeItems

    let chartStates = {}

    for(let state in states) {
        const stateUpperCase = state.toUpperCase()               
        // Исключаемые параметры, для которых график не строится
        if (excludeArr.includes(stateUpperCase) == false) {
            chartStates[state] = states[state]
            states[state].nameLocal = getTranslate.value['STATE_' + stateUpperCase] || state
        }
    }

    return chartStates
})

const UTACarHistoryFrom     = ref(calcDatas('tBegin'))
const UTACarHistoryTill     = ref(calcDatas('tEnd'))
const stateHistoryBuilding  = ref([])
const stateHistory          = ref({})
const stateHistoryIDs       = ref([])
const graphParams           = ref({})
const isShowResetZoomBtn    = ref(false)

function setGraphParams(event) {

    graphParams.value = event
}

function submitForm() {

    stateHistory.value = {}

    carHistoryStore.setStatesHistory(null)
    carHistoryStore.setStatesHistoryMsg({})

    let data = { 

        car: props.uuid,
        key_array: []
    }

    if(Object.keys(graphParams.value).length == 0) {

        carHistoryStore.setStatesHistoryMsg({ success: false, text: getTranslate.value.HISTORY_ERROR_PARAMS })
        return false
    }

    for(let key in graphParams.value) {

        data.key_array.push(key)
        stateHistory.value[key] = []
    }

    stateHistoryIDs.value = data.key_array

    if(UTACarHistoryFrom.value && UTACarHistoryTill.value) {

        const trackStart = (new Date(UTACarHistoryFrom.value).getTime()) / 1000
        const trackStop = (new Date(UTACarHistoryTill.value).getTime()) / 1000

        if(trackStart < trackStop) {

            if(trackStop - trackStart <= 2592000) {

                data.start = trackStart
                data.stop = trackStop
                data['c.limit'] = 1000

                carHistoryStore.readStatesHistory(data)

            } else {

                carHistoryStore.setStatesHistoryMsg({ success: false, text: getTranslate.value.HISTORY_ERROR_RANGE })
            }

        } else {

            carHistoryStore.setStatesHistoryMsg({ success: false, text: getTranslate.value.HISTORY_ERROR_TIME_INCORRECT })
        } 

    } else {

        carHistoryStore.setStatesHistoryMsg({ success: false, text: getTranslate.value.HISTORY_ERROR_TIME_EMPTY })
    }
}

function filterCarStates(input) {

    if(input && input.length > 2) {

        const filtered = {}

        for( let state in carStates.value ) {

            const re = new RegExp(input, 'gi')

            if( re.test( carStates.value[state].nameLocal ) ) {

                filtered[state] = carStates.value[state]
            }
        }
    }
}

filterCarStates()

onBeforeUnmount(() => {

    carHistoryStore.setStatesHistory(null)
    carHistoryStore.setStatesHistoryMsg({})
})

watch(statesHistoryPart, part => {

    if(part) {

        stateHistoryBuilding.value.push( ...part.array )

        if(part.c.isAfter) {

            carHistoryStore.readStatesHistory({

                'c.limit': 1000,
                car: props.uuid,
                start: (new Date(UTACarHistoryFrom.value).getTime()) / 1000, 
                stop: (new Date(UTACarHistoryTill.value).getTime()) / 1000,
                key_array: stateHistoryIDs.value,
                'c.cursor': part.c.stop,
                direction: 'after'
            })

        } else {

            stateHistoryBuilding.value.map( point => {

                if(point.s.length) {

                    point.s.map( item => {

                        let value = null

                        if( 'd' in item.value) value = Number(item.value.d) // Целое число
                        if( 'i' in item.value) value = Number(item.value.i) // Число с плавающей точкой
                        if( 's' in item.value) value = String(item.value.s) // Строка

                        stateHistory.value[item.key].push(
                            { 
                                date: new Date(Number(point.ntm) * 1000), 
                                value
                            }
                        )
                    })
                }
            })

            carHistoryStore.setStatesHistory(stateHistory.value)
            stateHistoryBuilding.value = []
        }

        carHistoryStore.setStatesHistoryPart(null)
    }
})
</script>

<template>
    <div class="uta-car-history">

        <header class="uta-page-header" role="heading" aria-level="1">
            <h1 class="uta-page-header__title">
                {{ getTranslate.HISTORY }}
            </h1>
        </header>

        <div 
            class="mb-4 uta-alert uta-alert--dark uta-alert--danger uta-car-history__message"
            v-if="'success' in statesHistoryMsg && !statesHistoryMsg.success"
        >
            <span class="material-icons uta-alert__icon">
                {{ statesHistoryMsg.success ? 'check_circle_outline' : 'error_outline' }}
            </span>
            <span class="uta-alert__text">{{ getTranslate[statesHistoryMsg.text] || statesHistoryMsg.text }}</span>
        </div>

        <form action="" @submit.prevent="submitForm($event)">

            <div class="row">
                <div class="form-group col-12 col-md-6 mb-4">
                    <label for="ADTCarHistoryStart">{{ getTranslate.HISTORY_START }}:</label>
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarHistoryFrom"
                        :max="UTACarHistoryMaxDateFrom"
                        v-model="UTACarHistoryFrom"
                    >
                </div>
                <div class="form-group col-12 col-md-6 mb-4">
                    <label for="ADTCarHistoryStop">{{ getTranslate.HISTORY_END }}:</label>
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarHistoryTill"
                        :max="UTACarHistoryMaxDateTill"
                        v-model="UTACarHistoryTill"
                    >
                </div>
            </div>

            <div v-if="profile.attr" class="uta-car-history__states row">
                <div class="col mb-4">
                    <label for="ADTStatesHistoryInput" class="uta-car-history__states-label">{{ getTranslate.HISTORY_PARAMS }}:</label>
                    <KANChosen :list="carStates" @chosenItems="setGraphParams($event)" />
                </div>
            </div>

            <div class="mt-4 uta-btns">

                <button type="submit" class="uta-btn uta-btn--main" :disabled="statesHistoryProcess">
                    
                    <template v-if="!statesHistoryProcess">
                        {{ getTranslate.BTN_REQUEST }}
                    </template>

                    <template v-else>
                        <div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </template>
                    
                </button>

            </div>

            <template v-if="statesHistory">

                <div class="adt-chart-history mt-4">
                    <template v-for="(state, stateKey) in statesHistory" :key="stateKey">
                        <UTACarHistoryGraph 
                            :state="state" 
                            :chartKey="stateKey" 
                            :graphsCount="Object.keys(statesHistory).length"
                            :isShowResetZoomBtn="isShowResetZoomBtn"
                            @showResetZoomBtn="isShowResetZoomBtn = true"
                            @hideResetZoomBtn="isShowResetZoomBtn = false"
                        /> 
                    </template>
                </div>

                <template v-if="statesHistory[Object.keys(statesHistory)[0]].length">

                    <div class="mt-4 uta-car-history__instruction">
                        {{ getTranslate.HISTORY_INSTRUCTION }}
                    </div>

                    <div class="mt-4 uta-car-history__panning">
                        {{ getTranslate.HISTORY_INSTRUCTION_PANNING }}
                    </div>

                </template>

            </template>

        </form>

    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-history';
</style>